import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import React from "react";
import { useTranslation } from "react-i18next";
import { ApiInternal } from "src/api/api_internal";
import SINGLE_TENANT_CONTEXT from "src/context/internal/tenant";
import TabLayout from "src/components/base/tab-layout";
import { Tab, TabMenu } from "src/components/base/tab-menu";
import { Button } from "src/components/base/button";
import { TrashIcon } from "@heroicons/react/20/solid";
import Outlet from "src/components/base/outlet";

/**
 * The properties for {@link TenantTabLayout}
 */
export type TenantTabLayoutProps = {};

/**
 * The layout for the tenant tab
 */
export default function TenantTabLayout(props: TenantTabLayoutProps) {
    const [t] = useTranslation("tenant");

    const navigate = useNavigate();
    const router = useRouter();
    const tenant = Route.useLoaderData();

    return (
        <SINGLE_TENANT_CONTEXT value={{ tenant, reset: async () => await router.invalidate({ sync: true }) }}>
            <TabLayout
                breadcrumbs={[
                    { label: t("button.tenants"), to: "/a/tenants" },
                    { label: tenant.name, to: "/a/tenants/$tenantId", params: { tenantId: tenant.uuid } },
                ]}
                heading={tenant.name}
                headingChildren={
                    <Button
                        outline={true}
                        onClick={() =>
                            navigate({
                                to: "/a/tenants/$tenantId/delete-preview",
                                params: { tenantId: tenant.uuid },
                            })
                        }
                    >
                        <TrashIcon />
                        {t("button.delete-tenant")}
                    </Button>
                }
                tabs={
                    <TabMenu>
                        <Tab href={"/a/tenants/$tenantId/general"} params={{ tenantId: tenant.uuid }}>
                            {t("heading.general")}
                        </Tab>
                        <Tab href={"/a/tenants/$tenantId/oidc"} params={{ tenantId: tenant.uuid }}>
                            {t("heading.oidc")}
                        </Tab>
                        <Tab href={"/a/tenants/$tenantId/ldap"} params={{ tenantId: tenant.uuid }}>
                            {t("heading.ldap")}
                        </Tab>
                        <Tab href={"/a/tenants/$tenantId/scim"} params={{ tenantId: tenant.uuid }}>
                            {t("heading.scim")}
                        </Tab>
                        <Tab href={"/a/tenants/$tenantId/split-dns"} params={{ tenantId: tenant.uuid }}>
                            {t("heading.split-dns")}
                        </Tab>
                        <Tab href={"/a/tenants/$tenantId/search-domain"} params={{ tenantId: tenant.uuid }}>
                            {t("heading.search-domain")}
                        </Tab>
                    </TabMenu>
                }
            >
                <Outlet />
            </TabLayout>
        </SINGLE_TENANT_CONTEXT>
    );
}

export const Route = createFileRoute("/_admin/a/tenants/$tenantId/_tenant")({
    component: TenantTabLayout,
    loader: async ({ params }) => await ApiInternal.tenants.getSingle(params.tenantId),
});
